/** Small, class-driven UI primitives shared across the dashboard. */

import {
  useEffect,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

type BtnVariant = 'primary' | 'ghost' | 'danger';

export function Button({
  variant = 'ghost',
  small,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; small?: boolean }) {
  return (
    <button
      className={`btn btn--${variant}${small ? ' btn--sm' : ''} ${className}`.trim()}
      {...rest}
    />
  );
}

export function EnvBadge({ environment }: { environment: 'live' | 'test' }) {
  return <span className={`badge badge--${environment}`}>{environment}</span>;
}

export function Chip({ children }: { children: ReactNode }) {
  return <span className="chip">{children}</span>;
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

export function Spinner() {
  return <div className="spinner" role="status" aria-label="Loading" />;
}

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="overlay" onMouseDown={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal__head">
          <h2 className="modal__title">{title}</h2>
          <button className="iconbtn" aria-label="Close" onClick={onClose}>
            <IconX />
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </div>
  );
}

export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1600);
    } catch {
      /* clipboard blocked — the value is visible for manual copy */
    }
  };
  return (
    <Button variant="ghost" small onClick={copy}>
      {done ? <IconCheck /> : <IconCopy />}
      {done ? 'Copied' : label}
    </Button>
  );
}

/** Ephemeral bottom toast. */
export function useToast() {
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const show = (kind: 'ok' | 'err', msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3200);
  };
  const node = toast ? (
    <div className={`toast toast--${toast.kind}`} role="status">
      {toast.kind === 'ok' ? <IconCheck /> : <IconAlert />}
      {toast.msg}
    </div>
  ) : null;
  return { node, ok: (m: string) => show('ok', m), err: (m: string) => show('err', m) };
}

/* ── Inline icons (currentColor, 16px) ─────────────────────────────────────── */
const S = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

export function IconX() {
  return (
    <svg {...S} aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
export function IconCopy() {
  return (
    <svg {...S} aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
export function IconCheck() {
  return (
    <svg {...S} aria-hidden>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
export function IconPlus() {
  return (
    <svg {...S} aria-hidden>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
export function IconAlert() {
  return (
    <svg {...S} aria-hidden>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}
